<template>
  <div class="sa-layout">

    <!-- 行動版頂部 header -->
    <header class="sa-topbar">
      <button class="sa-topbar__menu" @click="sidebarOpen = !sidebarOpen" aria-label="選單">
        <span class="sa-topbar__hamburger" :class="{ 'is-open': sidebarOpen }" />
      </button>
      <span class="sa-topbar__title">店家後台</span>
      <button class="sa-topbar__logout" @click="logout">登出</button>

      <!-- 新手引導：引導點選單（手機版，選單未打開時顯示） -->
      <div v-if="onboardingActive && !sidebarOpen && !checking && !showWelcome" class="sa-topbar__ob-hint">
        <span class="sa-topbar__ob-arrow">←</span>
        <span v-if="currentObStep === 'StoreAdminProfile'" class="sa-topbar__ob-text">先點這裡<br>店家資料</span>
        <span v-else-if="currentObStep === 'StoreAdminHours'" class="sa-topbar__ob-text">下一步<br>營業時間</span>
        <span v-else class="sa-topbar__ob-text">下一步<br>服務管理</span>
      </div>
    </header>

    <!-- 遮罩（行動版） -->
    <div
      v-if="sidebarOpen"
      class="sa-overlay"
      @click="sidebarOpen = false"
    />

    <!-- 側欄 -->
    <nav class="sa-sidebar" :class="{ 'is-open': sidebarOpen }">
      <div class="sa-sidebar__brand">
        <span class="sa-sidebar__brand-text">店家後台</span>
      </div>

      <ul class="sa-nav">
        <li v-for="item in navItems" :key="item.name">
          <router-link
            :to="{ name: item.name }"
            class="sa-nav__link"
            :class="{ 'sa-nav__link--ob': currentObStep === item.name }"
            active-class="sa-nav__link--active"
            @click="sidebarOpen = false"
          >
            <span class="sa-nav__icon">{{ item.icon }}</span>
            {{ item.label }}
            <span v-if="currentObStep === item.name" class="sa-nav__ob-step">
              {{ item.name === 'StoreAdminServices' ? '← 新增服務' : '← 下一步' }}
            </span>
          </router-link>
        </li>
      </ul>

      <div class="sa-sidebar__footer">
        <p class="sa-sidebar__account">{{ storeAdminStore.adminInfo?.account }}</p>
        <!-- 測試機專用：重設新手引導 -->
        <button v-if="isLocalhost" class="sa-sidebar__reset-btn" @click="resetOnboarding" title="清除引導記錄，模擬新店家">🔄 重設引導</button>
        <button class="sa-sidebar__logout-btn" @click="logout">登出</button>
      </div>
    </nav>

    <!-- 主內容 -->
    <!-- 回頂端按鈕（手機版，捲動超過 300px 才出現） -->
    <button
      v-if="showScrollTop"
      class="sa-scroll-top"
      @click="doScrollTop"
      aria-label="回頂端"
    >↑</button>

    <main class="sa-main" ref="mainRef">
      <!-- Token 驗證中 -->
      <div v-if="checking" class="sa-checking">
        <span class="sa-spinner" />
        <p>驗證登入狀態…</p>
      </div>
      <!-- 登入選擇：引導 or 直接進入（回訪店家） -->
      <div v-else-if="showWelcome" class="sa-welcome">
        <div class="sa-welcome__card">
          <p class="sa-welcome__emoji">👋</p>
          <h2 class="sa-welcome__title">歡迎回來！</h2>
          <p class="sa-welcome__sub">需要重新觀看設定引導嗎？</p>
          <div class="sa-welcome__actions">
            <button class="sa-welcome__btn sa-welcome__btn--guide" @click="chooseGuide">
              📖 重新觀看引導
            </button>
            <button class="sa-welcome__btn sa-welcome__btn--skip" @click="chooseSkip">
              直接進入後台
            </button>
          </div>
        </div>
      </div>

      <template v-else>
        <!-- 預約連結列 -->
        <div class="sa-booking-bar">
          <span class="sa-booking-bar__label">📎 客人預約連結</span>
          <a :href="bookingUrl" target="_blank" class="sa-booking-bar__url">{{ bookingUrl }}</a>
          <button class="sa-booking-bar__copy" @click="copyLink">{{ copied ? '✅ 已複製' : '複製連結' }}</button>
        </div>
        <router-view />
      </template>
    </main>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick, provide } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStoreAdminStore } from '@/store/storeAdminStore'

const router          = useRouter()
const route           = useRoute()
const storeAdminStore = useStoreAdminStore()

const checking      = ref(true)
const sidebarOpen   = ref(false)
const showWelcome   = ref(false)   // 回訪店家登入選擇畫面
const mainRef       = ref(null)    // 主內容捲軸容器
const showScrollTop = ref(false)   // 回頂端按鈕顯示控制

// 關掉瀏覽器自動還原捲軸位置，改由我們自己控制
if (typeof window !== 'undefined' && 'scrollRestoration' in history) {
  history.scrollRestoration = 'manual'
}

// ── 捲回頂端 ──────────────────────────────────────
function doScrollTop() {
  if (mainRef.value) mainRef.value.scrollTop = 0
  window.scrollTo(0, 0)
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
  showScrollTop.value = false
}

// 換頁：立刻捲 + 150ms 補一次（防止非同步內容撐開後跑掉）
router.afterEach(async () => {
  await nextTick()
  doScrollTop()
  setTimeout(doScrollTop, 150)
})

// ── 回頂端按鈕（捲超過 300px 才顯示）────────────
function onScroll() {
  const scrolled = window.scrollY || document.documentElement.scrollTop || (mainRef.value?.scrollTop ?? 0)
  showScrollTop.value = scrolled > 300
}
onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  mainRef.value?.addEventListener('scroll', onScroll, { passive: true })
})

// ── 新手引導：當前步驟 ────────────────────────────
// 換頁 / storeId 變動時重新計算，回傳目前應該做哪個步驟的 routeName
const currentObStep = ref(null)

function updateObStep() {
  const id = storeAdminStore.storeId
  if (!id) { currentObStep.value = null; return }
  if (localStorage.getItem(`jili_ob_skip_${id}`)) { currentObStep.value = null; return }
  if (!localStorage.getItem(`jili_ob_p_${id}`)) { currentObStep.value = 'StoreAdminProfile';  return }
  if (!localStorage.getItem(`jili_ob_h_${id}`)) { currentObStep.value = 'StoreAdminHours';    return }
  if (!localStorage.getItem(`jili_ob_s_${id}`)) { currentObStep.value = 'StoreAdminServices'; return }
  currentObStep.value = null  // 全部完成
}

// onboardingActive 從 currentObStep 衍生
const onboardingActive = computed(() => currentObStep.value !== null)

watch(() => route.name, () => { updateObStep() })

// 讓子頁面（Profile / Hours / Services）存完後可以主動更新
provide('updateObStep', updateObStep)

const bookingUrl = computed(() =>
  `https://jiliskin.com/booking/stores/${storeAdminStore.storeId}`
)
const copied = ref(false)
async function copyLink() {
  try {
    await navigator.clipboard.writeText(bookingUrl.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch {
    prompt('請手動複製以下連結：', bookingUrl.value)
  }
}

const navItems = [
  { name: 'StoreAdminAnnouncements', label: '公告',     icon: '📢' },
  { name: 'StoreAdminGuide',         label: '操作指南', icon: '📖' },
  { name: 'StoreAdminProfile',       label: '店家資料', icon: '🏠' },
  { name: 'StoreAdminHours',         label: '營業時間', icon: '🏪' },
  { name: 'StoreAdminServices',      label: '服務管理', icon: '✂️'  },
  { name: 'StoreAdminSlots',         label: '時段管理', icon: '🕐' },
  { name: 'StoreAdminReservations',  label: '預約管理', icon: '📅' },
  { name: 'StoreAdminDashboard',     label: '儀表板',  icon: '📊' },
]

onMounted(async () => {
  // 沒有 token → 直接跳登入
  if (!storeAdminStore.isLoggedIn) {
    router.replace({ name: 'StoreAdminLogin' })
    return
  }
  // 有 token → 呼叫 /me 驗證是否還有效
  const result = await storeAdminStore.fetchMe()
  if (result.unauthorized || !result.success) {
    router.replace({ name: 'StoreAdminLogin' })
    return
  }
  checking.value = false

  // 新店家引導：先設定指示點狀態，再決定要不要轉頁
  const obKey = `jili_ob_p_${storeAdminStore.storeId || ''}`

  // 若伺服器有 store_name（店家曾儲存過資料）但 localStorage 被清除，自動補回 key
  if (result.store_name && !localStorage.getItem(obKey)) {
    localStorage.setItem(obKey, '1')
  }

  updateObStep()

  const skipKey = `jili_ob_skip_${storeAdminStore.storeId || ''}`
  const currentRoute = router.currentRoute.value.name

  if (currentObStep.value === 'StoreAdminProfile') {
    // 全新店家（尚未儲存店家資料）→ 導向操作指南
    if (currentRoute !== 'StoreAdminGuide') {
      router.replace({ name: 'StoreAdminGuide' })
    }
  } else if (localStorage.getItem(skipKey)) {
    // 已選「直接進入」的回訪店家 → 停留在目前頁面（不強制跳轉）
    // 什麼都不做，讓使用者待在原本打開的頁面
  } else {
    // 回訪店家尚未選擇 → 顯示歡迎選擇畫面
    showWelcome.value = true
  }
})

// 回訪店家：選擇重看引導（清除所有 ob 記錄，包含 skip）
function chooseGuide() {
  Object.keys(localStorage)
    .filter(k => k.startsWith('jili_ob_'))
    .forEach(k => localStorage.removeItem(k))
  updateObStep()
  showWelcome.value = false
  router.replace({ name: 'StoreAdminGuide' })
}
// 回訪店家：直接進入（記住選擇，下次不再詢問）
function chooseSkip() {
  const skipKey = `jili_ob_skip_${storeAdminStore.storeId || ''}`
  localStorage.setItem(skipKey, '1')
  showWelcome.value = false
  router.replace({ name: 'StoreAdminGuide' })
}

async function logout() {
  storeAdminStore.logout()
  router.replace({ name: 'StoreAdminLogin' })
}

// 測試機專用
const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost'
function resetOnboarding() {
  Object.keys(localStorage)
    .filter(k => k.startsWith('jili_ob_'))
    .forEach(k => localStorage.removeItem(k))
  updateObStep()
  showWelcome.value = false
  router.replace({ name: 'StoreAdminGuide' })
}

</script>

<style scoped>
/* ── 整體佈局 ─────────────────────────────────────── */
.sa-layout {
  display: flex;
  min-height: 100vh;
  background: #f4f6f9;
}

/* ── 頂部 bar（行動版）─────────────────────────────── */
.sa-topbar {
  display: none;
  position: fixed;
  top: 0; left: 0; right: 0;
  height: 52px;
  background: #2c3e50;
  color: #fff;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  z-index: 200;
  box-shadow: 0 1px 6px rgba(0,0,0,0.18);
}
.sa-topbar__title { font-size: 1rem; font-weight: 600; }
.sa-topbar__menu {
  background: none; border: none; cursor: pointer; padding: 6px;
}
.sa-topbar__hamburger,
.sa-topbar__hamburger::before,
.sa-topbar__hamburger::after {
  display: block;
  width: 22px; height: 2px;
  background: #fff;
  transition: transform 0.25s;
  position: relative;
}
.sa-topbar__hamburger::before,
.sa-topbar__hamburger::after {
  content: '';
  position: absolute;
}
.sa-topbar__hamburger::before { top: -7px; }
.sa-topbar__hamburger::after  { top:  7px; }
.sa-topbar__hamburger.is-open              { background: transparent; }
.sa-topbar__hamburger.is-open::before { transform: rotate(45deg)  translate(0, 7px); }
.sa-topbar__hamburger.is-open::after  { transform: rotate(-45deg) translate(0, -7px); }
.sa-topbar__logout {
  background: none; border: none; color: #ccc;
  font-size: 0.85rem; cursor: pointer;
}
.sa-topbar__logout:hover { color: #fff; }

/* 新手引導：漢堡選單提示 */
.sa-topbar__ob-hint {
  display: none;
}
@media (max-width: 768px) {
  .sa-topbar__ob-hint {
    display: flex;
    align-items: center;
    gap: 4px;
    position: absolute;
    left: 48px;
    top: 50%;
    transform: translateY(-50%);
    background: #f97316;
    color: #fff;
    border-radius: 8px;
    padding: 5px 10px;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.3;
    white-space: nowrap;
    box-shadow: 0 2px 10px rgba(249,115,22,0.5);
    animation: sa-ob-hint-bounce 0.9s ease-in-out infinite;
    pointer-events: none;
    z-index: 210;
  }
  .sa-topbar__ob-arrow {
    font-size: 1.1rem;
    animation: sa-ob-arrow-nudge 0.9s ease-in-out infinite;
  }
  @keyframes sa-ob-hint-bounce {
    0%, 100% { transform: translateY(-50%) translateX(0); }
    50%       { transform: translateY(-50%) translateX(-4px); }
  }
  @keyframes sa-ob-arrow-nudge {
    0%, 100% { transform: translateX(0); }
    50%       { transform: translateX(-3px); }
  }
}

/* ── 側欄 ─────────────────────────────────────────── */
.sa-sidebar {
  width: 220px;
  min-height: 100vh;
  background: #2c3e50;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
}
.sa-sidebar__brand {
  padding: 24px 20px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.sa-sidebar__brand-text {
  font-size: 1.05rem;
  font-weight: 700;
  color: #ecf0f1;
  letter-spacing: 0.03em;
}

/* 導覽連結 */
.sa-nav {
  list-style: none;
  padding: 12px 0;
  margin: 0;
  flex: 1;
}
.sa-nav__link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 20px;
  color: #b2bec3;
  text-decoration: none;
  font-size: 0.93rem;
  transition: background 0.15s, color 0.15s;
  border-radius: 0;
}
.sa-nav__link:hover { background: rgba(255,255,255,0.06); color: #ecf0f1; }
.sa-nav__link--active { background: rgba(255,255,255,0.12); color: #fff; font-weight: 600; }
.sa-nav__icon { font-size: 1rem; width: 20px; text-align: center; }

/* 新手引導：當前步驟高亮 */
.sa-nav__link--ob {
  background: rgba(249,115,22,0.15) !important;
  color: #fed7aa !important;
  animation: ob-link-glow 1.8s ease-in-out infinite;
}
.sa-nav__ob-step {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 700;
  color: #f97316;
  background: rgba(249,115,22,0.18);
  border-radius: 10px;
  padding: 2px 7px;
  animation: ob-step-bounce 0.9s ease-in-out infinite;
}
@keyframes ob-link-glow {
  0%, 100% { background: rgba(249,115,22,0.12); }
  50%       { background: rgba(249,115,22,0.25); }
}
@keyframes ob-step-bounce {
  0%, 100% { transform: translateX(0); }
  50%       { transform: translateX(3px); }
}

/* 側欄底部 */
.sa-sidebar__footer {
  padding: 16px 20px;
  border-top: 1px solid rgba(255,255,255,0.08);
}
.sa-sidebar__account {
  font-size: 0.8rem;
  color: #7f8c8d;
  margin: 0 0 8px;
  word-break: break-all;
}
.sa-sidebar__reset-btn {
  background: none;
  border: 1px solid rgba(245,158,11,0.4);
  color: #fcd34d;
  font-size: 0.78rem;
  padding: 4px 10px;
  border-radius: 5px;
  cursor: pointer;
  width: 100%;
  margin-bottom: 6px;
  transition: background 0.15s;
}
.sa-sidebar__reset-btn:hover { background: rgba(245,158,11,0.15); }
.sa-sidebar__logout-btn {
  background: none;
  border: 1px solid rgba(255,255,255,0.2);
  color: #b2bec3;
  font-size: 0.82rem;
  padding: 5px 14px;
  border-radius: 5px;
  cursor: pointer;
  width: 100%;
  transition: background 0.15s;
}
.sa-sidebar__logout-btn:hover { background: rgba(255,255,255,0.08); color: #ecf0f1; }

/* ── 主內容 ──────────────────────────────────────── */
.sa-main {
  flex: 1;
  min-width: 0;
  padding: 72px 28px 32px;
  overflow-y: auto;
}

/* ── 預約連結列 ──────────────────────────────────── */
.sa-booking-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  background: #f0faf4;
  border-bottom: 1px solid #a8ddb8;
  padding: 10px 20px;
}
.sa-booking-bar__label { font-size: 0.85rem; font-weight: 700; color: #2d7a4f; white-space: nowrap; }
.sa-booking-bar__url { font-size: 0.85rem; color: #1a6b9a; text-decoration: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sa-booking-bar__url:hover { text-decoration: underline; }
.sa-booking-bar__copy { background: #2d7a4f; color: #fff; border: none; border-radius: 5px; font-size: 0.82rem; padding: 5px 12px; cursor: pointer; white-space: nowrap; flex-shrink: 0; transition: background 0.15s; }
.sa-booking-bar__copy:hover { background: #235f3d; }

/* ── 登入選擇畫面 ────────────────────────────────── */
.sa-welcome {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 72px);
}
.sa-welcome__card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.10);
  padding: 48px 40px;
  text-align: center;
  max-width: 380px;
  width: 100%;
}
.sa-welcome__emoji { font-size: 2.5rem; margin: 0 0 12px; }
.sa-welcome__title { font-size: 1.4rem; color: #2c3e50; margin: 0 0 8px; }
.sa-welcome__sub   { font-size: 0.92rem; color: #7f8c8d; margin: 0 0 28px; }
.sa-welcome__actions { display: flex; flex-direction: column; gap: 12px; }
.sa-welcome__btn {
  padding: 13px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background 0.15s;
}
.sa-welcome__btn--guide {
  background: #2c3e50;
  color: #fff;
}
.sa-welcome__btn--guide:hover { background: #34495e; }
.sa-welcome__btn--skip {
  background: transparent;
  border: 1px solid #dce1e7;
  color: #7f8c8d;
}
.sa-welcome__btn--skip:hover { background: #f4f6f9; color: #555; }

/* ── 驗證中 ──────────────────────────────────────── */
.sa-checking {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px;
  color: #888;
  gap: 16px;
}
.sa-spinner {
  display: inline-block;
  width: 32px; height: 32px;
  border: 3px solid #ddd;
  border-top-color: #8b6f5c;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── 遮罩（行動版側欄開啟時）────────────────────── */
.sa-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 150;
}

/* ── 回頂端按鈕（手機版）───────────────────────── */
.sa-scroll-top {
  display: none;
}
@media (max-width: 768px) {
  .sa-scroll-top {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: fixed;
    right: 16px;
    bottom: 90px;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(44,62,80,0.80);
    color: #fff;
    border: none;
    box-shadow: 0 2px 10px rgba(0,0,0,0.22);
    cursor: pointer;
    z-index: 89;
    backdrop-filter: blur(4px);
    transition: transform 0.15s;
    gap: 1px;
  }
  .sa-scroll-top:active { transform: scale(0.92); }
  .sa-scroll-top { font-size: 1.1rem; font-weight: 700; }
}

/* ── 響應式 ─────────────────────────────────────── */
@media (max-width: 768px) {
  .sa-topbar  { display: flex; }
  .sa-overlay { display: block; }

  .sa-sidebar {
    position: fixed;
    top: 0; left: 0;
    height: 100vh;
    z-index: 180;
    transform: translateX(-100%);
    transition: transform 0.25s ease;
  }
  .sa-sidebar.is-open { transform: translateX(0); }

  .sa-booking-bar {
    top: 52px;
    flex-wrap: nowrap;
    padding: 8px 12px;
    gap: 8px;
  }
  .sa-booking-bar__url { display: none; }

  .sa-main { padding: 108px 16px 32px; }
}
</style>
